/**
 * Chart Data Transformation Module
 *
 * Transforms SQL result rows into chart-ready data structures with series configuration.
 *
 * Core Algorithm:
 * 1. Build ordered array of grouping elements (measures + dimensions)
 * 2. Date dimension always moves to end (becomes X-axis)
 * 3. Last element = X-axis grouping, all others = Series keys
 * 4. Group rows by X-axis, pivot series into columns
 *
 * Special Cases:
 * - Benchmark: DataSource as X-axis, custom sorting
 * - Attrition Drivers: Feature as X-axis, fixed series key
 * - Projections: Only applicable with time dimensions
 */

import {
  DIMENSIONS,
  DimensionV2,
  findTimeDimensionV2,
} from "@/common/dimensions.ts";
import {
  AXIS_GROUP_LABELS,
  AxisGroup,
  MeasureName,
  MEASURES,
  MeasureV2,
} from "@/common/measures.ts";
import { FILTERS, findTimeFilterV2 } from "@/common/filters.ts";
import { formatDate } from "@/common/dateUtils.ts";
import { assignSeriesColorsV2 } from "@/common/colorUtils.ts";
import { SeriesConfig } from "@/common/type/report-types.ts";
import { ReportConfigV2 } from "@/common/report-configuration.ts";
import { LIKERT_SCALE_LABELS } from "./constants.ts";

export interface DataItem {
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  value: number;
  value_num_employees?: number;
  value_pct?: number;
  MEASURE?: MeasureName;
  projected?: boolean;
  axis_group?: string;
}

export const getPercentChange = (
  currentValue: unknown,
  previousValue: unknown,
): number | null => {
  if (currentValue == null || previousValue == null) {
    return null;
  }

  const current = Number(currentValue);
  const previous = Number(previousValue);
  if (
    !Number.isFinite(current) ||
    !Number.isFinite(previous) ||
    previous === 0
  ) {
    return null;
  }

  return ((current - previous) / previous) * 100;
};

interface TransformConfig {
  reportConfig: ReportConfigV2;
  measures: MeasureV2[];
  MEASURES: Record<MeasureName, MeasureV2>;
  dimensions: DimensionV2[];
}

interface GroupingElement {
  type: "measure" | "dimension";
  field: string;
  label: string;
  isTimeDimension: boolean;
  dimension?: DimensionV2;
}

interface SeriesKeyMetadata {
  axisId: string;
  measureLabel: string;
  measure: MeasureV2;
  isProjected?: boolean;
  isPreviousPeriod?: boolean;
}

interface ChartDataRow {
  name: string;
  [key: string]: number | string;
}

/**
 * Main transformation function - routes to appropriate handler
 */
export function transformToChartData(
  limitedData: DataItem[],
  reportConfig: ReportConfigV2,
): { chartData: ChartDataRow[]; seriesConfig: SeriesConfig } {
  if (!limitedData?.length) {
    return { chartData: [], seriesConfig: new Map() };
  }
  const dimensions = reportConfig.data.dimensions.map((dim) => DIMENSIONS[dim]),
    measures = reportConfig.data.measures.map((meas) => MEASURES[meas]);

  const config: TransformConfig = {
    reportConfig,
    measures: measures as MeasureV2[],
    dimensions,
    MEASURES: MEASURES as Record<MeasureName, MeasureV2>,
  };

  // Handle special dataset exceptions
  if (reportConfig.data.datasetName === "benchmark") {
    return transformBenchmarkData(limitedData, config);
  }

  if (
    reportConfig.data.datasetName === "attrition_drivers" ||
    reportConfig.data.datasetName === "time_to_fill_drivers"
  ) {
    return transformAttritionDriversData(limitedData, config);
  }

  // Standard transformation
  return transformStandardData(limitedData, config);
}

/**
 * Standard data transformation (non-benchmark, non-attrition-drivers)
 */
function transformStandardData(
  limitedData: DataItem[],
  config: TransformConfig,
): { chartData: ChartDataRow[]; seriesConfig: SeriesConfig } {
  const { reportConfig, measures, dimensions, MEASURES } = config,
    hasTimeDimension = findTimeDimensionV2(reportConfig.data) != null,
    isGroupedBarChart = ["Bar", "Horizontal Bar"].includes(
      reportConfig.chart.chartType,
    ),
    isComparison = reportConfig.data.comparisonEnabled;

  let previousSnapshotDate = undefined,
    filterField: string = "";

  if (isComparison && isGroupedBarChart && !hasTimeDimension) {
    // If this a previous period comparison, we there will be two unique snapshot dates,
    // and the date column will be that of the filter used.
    const dateFilter = findTimeFilterV2(reportConfig.data);
    if (!dateFilter) {
      throw new Error("Date filter not found for previous period comparison");
    }
    filterField = FILTERS[dateFilter.filterDefinitionName].field;
    const snapshotDates = Array.from(
      new Set(
        limitedData
          .map((row) => row[filterField])
          .filter(
            (date): date is string =>
              typeof date === "string" && !Number.isNaN(Date.parse(date)),
          ),
      ),
    );
    if (snapshotDates.length > 1) {
      previousSnapshotDate = snapshotDates.sort(
        (a, b) => Date.parse(a) - Date.parse(b),
      )[0];
    }
  }

  // Build ordered elements with date last
  const { xAxisElement, seriesElements, hasMultipleMeasures } =
    buildOrderedElements(measures, dimensions);

  // Phase 1: Collect all unique series keys for color assignment
  const allSeriesKeys = new Set<string>();
  const previousSeriesKeys = new Set<string>();
  const seriesMetadata = new Map<string, SeriesKeyMetadata>();

  // During dataset changes there is a race condition where limiteData
  // may contain measures not in the current config
  if (
    limitedData.some(
      (row) => !row.MEASURE || !MEASURES[row.MEASURE as MeasureName],
    )
  ) {
    return { chartData: [], seriesConfig: new Map() };
  }

  limitedData.forEach((row) => {
    const measure = MEASURES[row.MEASURE as MeasureName];
    const seriesKey = buildSeriesKey(
      row,
      seriesElements,
      hasMultipleMeasures,
      MEASURES,
    );

    allSeriesKeys.add(seriesKey);
    if (previousSnapshotDate && row[filterField] === previousSnapshotDate) {
      previousSeriesKeys.add(seriesKey);
    }

    // Store metadata for seriesConfig building
    if (!seriesMetadata.has(seriesKey)) {
      seriesMetadata.set(seriesKey, {
        axisId: measure.axisGroup,
        measureLabel: measure.label,
        measure,
      });
    }

    // Handle projected variants (TIME-ONLY)
    if (
      hasTimeDimension &&
      reportConfig.data.includeProjections &&
      row.projected
    ) {
      const projectedKey = `${seriesKey}_projected`;
      allSeriesKeys.add(projectedKey);
      seriesMetadata.set(projectedKey, {
        axisId: measure.axisGroup,
        measureLabel: measure.label,
        measure,
        isProjected: true,
      });
    }
  });

  if (previousSnapshotDate) {
    const orderedSeriesKeys: string[] = [];
    Array.from(allSeriesKeys).forEach((seriesKey) => {
      if (previousSeriesKeys.has(seriesKey)) {
        const previousKey = `${seriesKey}_previous`;
        orderedSeriesKeys.push(previousKey, seriesKey);
        seriesMetadata.set(previousKey, {
          ...seriesMetadata.get(seriesKey)!,
          isPreviousPeriod: true,
        });
      } else {
        orderedSeriesKeys.push(seriesKey);
      }
    });
    allSeriesKeys.clear();
    orderedSeriesKeys.forEach((seriesKey) => allSeriesKeys.add(seriesKey));
  }

  // Assign colors to all series
  const seriesColorMap = assignSeriesColorsV2(
    Array.from(allSeriesKeys).filter((seriesKey) =>
      previousSnapshotDate ? !seriesKey.endsWith("_previous") : true,
    ),
    reportConfig,
  );

  // Phase 2: Group by X-axis value
  const groupedData = new Map<string, ChartDataRow>();

  limitedData.forEach((row) => {
    // Get X-axis value (grouping key)
    const xValue = getXAxisValue(row, xAxisElement, MEASURES);
    if (!xValue) return; // Skip rows with missing X-axis value

    // Get series key
    let seriesKey = buildSeriesKey(
      row,
      seriesElements,
      hasMultipleMeasures,
      MEASURES,
    );

    // Add _projected suffix if applicable (TIME-ONLY)
    if (
      hasTimeDimension &&
      reportConfig.data.includeProjections &&
      row.projected
    ) {
      seriesKey = `${seriesKey}_projected`;
    }

    if (previousSnapshotDate && row[filterField] === previousSnapshotDate) {
      seriesKey = `${seriesKey}_previous`;
    }

    // Initialize group if needed
    if (!groupedData.has(xValue)) {
      groupedData.set(xValue, {
        name: formatXAxisValue(xValue, xAxisElement),
        // rawXValue lets charts map a clicked tick back to the period start date
        ...(xAxisElement.isTimeDimension ? { rawXValue: xValue } : {}),
        ...(row.axis_group ? { axis_group: row.axis_group } : {}),
      });
    }

    const group = groupedData.get(xValue)!;

    // Add value to series column
    group[seriesKey] = row.value;

    // Add employee count if available (only for non-projected)
    if (!row.projected && row.value_num_employees !== undefined) {
      group[`${seriesKey}_employees`] = row.value_num_employees;
    }
  });

  // Phase 3: Build seriesConfig Map
  const seriesConfig: SeriesConfig = new Map();

  allSeriesKeys.forEach((seriesKey) => {
    const metadata = seriesMetadata.get(seriesKey)!;
    const baseKey = seriesKey.replace(/_(projected|previous)$/, "");
    const isProjected = metadata.isProjected || false;
    const isPreviousPeriod = metadata.isPreviousPeriod || false;

    const legendOverride = reportConfig.chart.legendItems[seriesKey];
    const baseLegendOverride = reportConfig.chart.legendItems[baseKey];
    const color =
      legendOverride?.color ||
      (isPreviousPeriod ? baseLegendOverride?.color : undefined) ||
      seriesColorMap.get(isPreviousPeriod ? baseKey : seriesKey) ||
      "#000000";
    const axisId = metadata.axisId as AxisGroup;

    // Compute tooltip metadata
    const employeeDataKey = isPreviousPeriod
      ? `${seriesKey}_employees`
      : `${baseKey}_employees`;
    const hasEmployeeData = Array.from(groupedData.values()).some(
      (group) => group[employeeDataKey] !== undefined,
    );

    // Use measure's showEmployeeCount property, but never show for projected data
    const showEmployees =
      metadata.measure.showEmployeeCount && !isProjected && hasEmployeeData;

    // Use AXIS_GROUP_LABELS for "MEASURE" series key (zero-dimension multi-measure case)
    const defaultLabel = (() => {
      if (baseKey === "MEASURE") {
        if (
          metadata.measure.axisLabelOverrideCount &&
          reportConfig.chart.showCount
        ) {
          return metadata.measure.axisLabelOverrideCount;
        }

        if (
          metadata.measure.axisLabelOverridePercent &&
          !reportConfig.chart.showCount
        ) {
          return metadata.measure.axisLabelOverridePercent;
        }
        return AXIS_GROUP_LABELS[axisId];
      } else if (metadata.measure.measure === "SURVEY_SCORE") {
        // Special case for SURVEY_SCORE to display the Likert label instead of the raw value
        if (dimensions.length > 1) {
          return LIKERT_SCALE_LABELS[baseKey];
        } else {
          return AXIS_GROUP_LABELS[axisId];
        }
      } else {
        return baseKey;
      }
    })();
    const label = isPreviousPeriod
      ? `${baseLegendOverride?.label || defaultLabel} (Prev.)`
      : defaultLabel;

    seriesConfig.set(seriesKey, {
      key: isProjected ? baseKey : seriesKey,
      label: legendOverride?.label || label,
      color,
      axisId,
      isProjected,
      ...(isPreviousPeriod ? { isPreviousPeriod: true } : {}),
      hidden:
        legendOverride?.hidden ??
        (isPreviousPeriod ? false : baseLegendOverride?.hidden || false),
      tooltip: {
        showEmployeeCount: showEmployees,
        hasEmployeeData,
      },
      measure: metadata.measure,
    });
  });

  const chartData = Array.from(groupedData.values());

  hydrateData(chartData, seriesConfig);

  return { chartData, seriesConfig };
}

/**
 * Fills in missing data points with zeroes for proper chart rendering
 * Checks the non-projected series keys for each object. If all keys
 * are not present, take no action, as this is a projected data point,
 * otherwise, fill in missing keys with zeroes.
 */
function hydrateData(chartData: ChartDataRow[], seriesConfig: SeriesConfig) {
  const nonProjectedKeys = Array.from(seriesConfig.values())
    .filter((s) => !s.isProjected)
    .map((s) => s.key);

  chartData.forEach((dataPoint) => {
    const hasAnyKey = nonProjectedKeys.some((key) => key in dataPoint);
    if (hasAnyKey) {
      nonProjectedKeys.forEach((key) => {
        if (!(key in dataPoint)) {
          dataPoint[key] = 0;
        }
      });
    }
  });
}

/**
 * Builds ordered array of grouping elements with Date last if present
 *
 * Returns: { xAxisElement, seriesElements, hasMultipleMeasures }
 */
function buildOrderedElements(
  measures: MeasureV2[],
  dimensions: DimensionV2[],
): {
  xAxisElement: GroupingElement;
  seriesElements: GroupingElement[];
  hasMultipleMeasures: boolean;
} {
  const hasMultipleMeasures = measures.length > 1;
  const elements: GroupingElement[] = [];

  // Add dimensions in configured order
  dimensions.forEach((dim) => {
    elements.push({
      type: "dimension",
      field: dim.field,
      label: dim.label,
      isTimeDimension: dim.isTimeDimension,
      dimension: dim,
    });
  });

  // Move date to end if present (becomes X-axis)
  const dateIndex = elements.findIndex((el) => el.isTimeDimension);
  if (dateIndex !== -1 && dateIndex !== elements.length - 1) {
    const [dateElement] = elements.splice(dateIndex, 1);
    elements.push(dateElement);
  }

  // Determine X-axis and series elements
  let xAxisElement: GroupingElement;
  let seriesElements: GroupingElement[];

  if (elements.length === 0) {
    // Zero dimensions: Measure labels are both series keys AND x-axis groups
    xAxisElement = {
      type: "measure",
      field: "MEASURE",
      label: "MEASURE",
      isTimeDimension: false,
    };
    seriesElements = [];
  } else if (elements.length === 1 && !hasMultipleMeasures) {
    // Single measure, single dimension: dimension is X-axis, no series differentiation
    xAxisElement = elements[0];
    seriesElements = [];
  } else {
    // Multiple elements OR multiple measures: last is X-axis, others form series keys
    xAxisElement = elements[elements.length - 1];
    seriesElements = elements.slice(0, -1);
  }

  return { xAxisElement, seriesElements, hasMultipleMeasures };
}

/**
 * Builds series key for legend/tooltip display
 *
 * The series key identifies each data series in the chart for coloring, legend display,
 * and tooltip rendering. It combines measure labels and dimension values based on the
 * configuration.
 *
 * Logic Flow:
 * 1. Single measure + zero seriesElements → Returns measure.label
 * 2. Single measure + seriesElements → Returns dimension value(s) joined by " | "
 * 3. Multiple measures + zero seriesElements → Returns measure.label for each measure
 * 4. Multiple measures + seriesElements → Returns measure.label + " | " + dimension values
 *
 * Examples:
 * - Single measure (Turnover), no dimensions: "Turnover Rate"
 * - Single measure (Turnover), dimensions [Gender=Female]: "Female"
 * - Single measure (Turnover), dimensions [Gender=Female, Department=Sales]: "Female | Sales"
 * - Multiple measures [Turnover, Headcount], no dimensions: "Turnover Rate", "Headcount"
 * - Multiple measures [Turnover], dimensions [Gender=Female]: "Turnover Rate | Female"
 *
 * @param row - Data row containing MEASURE field and dimension values
 * @param seriesElements - Non-X-axis grouping elements (dimensions only)
 * @param hasMultipleMeasures - Whether multiple measures are selected in the report
 * @param MEASURES - Lookup map for measure metadata
 * @returns Formatted series key string
 */
function buildSeriesKey(
  row: DataItem,
  seriesElements: GroupingElement[],
  hasMultipleMeasures: boolean,
  MEASURES: Record<MeasureName, MeasureV2>,
): string {
  const measure = MEASURES[row.MEASURE as MeasureName];

  // Zero series elements case (no dimensions to differentiate series)
  if (seriesElements.length === 0 && !hasMultipleMeasures) {
    return measure.label;
  }

  const keyParts: string[] = [];

  // If multiple measures, always lead with measure label
  if (hasMultipleMeasures) {
    keyParts.push(measure.label);
  }

  // Add dimension values (these are already the non-X-axis dimensions)
  seriesElements
    .filter((el) => el.type === "dimension")
    .forEach((el) => {
      keyParts.push(row[el.field]);
    });

  // Join with " | " for human readability
  return keyParts.join(" | ");
}

/**
 * Gets X-axis value from row
 */
function getXAxisValue(
  row: DataItem,
  xAxisElement: GroupingElement,
  MEASURES: Record<MeasureName, MeasureV2>,
): string {
  if (xAxisElement.type === "measure") {
    // Zero-dimension case: measure label is X-axis
    return MEASURES[row.MEASURE as MeasureName].label;
  } else {
    return row[xAxisElement.field];
  }
}

/**
 * Formats X-axis value for display
 */
function formatXAxisValue(
  value: string,
  xAxisElement: GroupingElement,
): string {
  if (xAxisElement.isTimeDimension) {
    return formatDate(value);
  }
  return value;
}

/**
 * Special handler for benchmark dataset
 */
function transformBenchmarkData(
  limitedData: DataItem[],
  config: TransformConfig,
): { chartData: ChartDataRow[]; seriesConfig: SeriesConfig } {
  const { reportConfig, measures, dimensions } = config;

  const groupedData = new Map<string, ChartDataRow & { _total?: number }>();
  const seriesValueTotals = new Map<string, number>();

  // Group by DataSource (X-axis)
  limitedData.forEach((row) => {
    const dataSource = row["DataSource"];
    const seriesKey = dimensions.map((dim) => row[dim.label]).join(" | ");

    if (!groupedData.has(dataSource)) {
      groupedData.set(dataSource, { name: dataSource, _total: 0 });
    }

    const group = groupedData.get(dataSource)!;
    if (row.value_pct !== undefined) {
      group[seriesKey] = row.value_pct;
    }
    if (row.value_num_employees !== undefined) {
      group[`${seriesKey}_employees`] = row.value_num_employees;
    }
    group._total = (group._total || 0) + (row.value_pct || 0);

    seriesValueTotals.set(
      seriesKey,
      (seriesValueTotals.get(seriesKey) || 0) + (row.value_pct || 0),
    );
  });

  // Custom sorting for benchmark
  const sortedData = Array.from(groupedData.values()).sort((a, b) => {
    if (a.name === "Benchmarking") return -1;
    if (b.name === "Benchmarking") return 1;
    if (a.name === "Your Representation") return -1;
    if (b.name === "Your Representation") return 1;
    return (b._total || 0) - (a._total || 0);
  });

  // Build seriesConfig
  const sortedSeriesValues = Array.from(seriesValueTotals.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([key]) => key);

  const seriesColorMap = assignSeriesColorsV2(sortedSeriesValues, reportConfig);

  const axisId = measures[0].axisGroup;
  const hasEmployeeData = false;
  // Use measure's showEmployeeCount property
  const showEmployees = measures[0].showEmployeeCount && hasEmployeeData;

  const seriesConfig: SeriesConfig = new Map(
    sortedSeriesValues.map((series) => {
      const color = seriesColorMap.get(series) || "#000000";

      return [
        series,
        {
          key: series,
          label:
            reportConfig.chart.legendItems[series]?.label || String(series),
          color: reportConfig.chart.legendItems[series]?.color || color,
          axisId,
          hidden: reportConfig.chart.legendItems[series]?.hidden || false,
          isProjected: false,
          tooltip: {
            showEmployeeCount: showEmployees,
            hasEmployeeData,
          },
          measure: measures[0],
        },
      ];
    }),
  );

  // Remove _total helper field before returning
  const cleanedData = sortedData.map((row) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _total, ...cleanRow } = row;
    return cleanRow as ChartDataRow;
  });

  return { chartData: cleanedData, seriesConfig };
}

/**
 * Special handler for attrition_drivers dataset
 */
function transformAttritionDriversData(
  limitedData: DataItem[],
  config: TransformConfig,
): { chartData: ChartDataRow[]; seriesConfig: SeriesConfig } {
  const { reportConfig, measures } = config;
  const groupedData = new Map<string, ChartDataRow>();
  const seriesKey = "Importance Score";

  // Group by Feature (X-axis)
  limitedData.forEach((row) => {
    const feature = row.Feature;

    if (!groupedData.has(feature)) {
      groupedData.set(feature, {
        name: feature,
        ...(row.axis_group ? { axis_group: row.axis_group } : {}),
      });
    }

    const group = groupedData.get(feature)!;
    group[seriesKey] = row.value;
    if (row.value_num_employees !== undefined) {
      group[`${seriesKey}_employees`] = row.value_num_employees;
    }
  });

  // Build seriesConfig
  const seriesColorMap = assignSeriesColorsV2([seriesKey], reportConfig);
  const color = seriesColorMap.get(seriesKey) || "#000000";
  const axisId = measures[0].axisGroup;

  const hasEmployeeData = Array.from(groupedData.values()).some(
    (group) => group[`${seriesKey}_employees`] !== undefined,
  );
  // Use measure's showEmployeeCount property
  const showEmployees = measures[0].showEmployeeCount && hasEmployeeData;

  const seriesConfig: SeriesConfig = new Map([
    [
      seriesKey,
      {
        key: seriesKey,
        label: reportConfig.chart.legendItems[seriesKey]?.label || seriesKey,
        color: reportConfig.chart.legendItems[seriesKey]?.color || color,
        axisId,
        hidden: reportConfig.chart.legendItems[seriesKey]?.hidden || false,
        isProjected: false,
        tooltip: {
          showEmployeeCount: showEmployees,
          hasEmployeeData,
        },
        measure: measures[0],
      },
    ],
  ]);

  const chartData = Array.from(groupedData.values());

  return { chartData, seriesConfig };
}
